import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AddNewsArticle, UpdateNewsArticle } from '../types/types'
import { useAuth } from './useAuth'
import { BASE_URL } from '../constants'
import { throwOnError } from '../utils/throwOnError'

export const useGetNewsArticlesQuery = () => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['news'],
    queryFn: async () => {
      const { data, error } = await authFetch().GET('/news')
      if (error) {
        throwOnError(error)
      }
      return data
    }
  })
}

export const useGetNewsArticleProveTaQuery = () => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['newsLatest', 'proveta'],
    queryFn: async () => {
      const { data, error } = await authFetch().GET('/news/latest/proveta')
      if (error) {
        throwOnError(error)
      }
      return data ?? []
    }
  })
}

export const useGetNewsArticleProvePlanQuery = () => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['newsLatest', 'proveplan'],
    queryFn: async () => {
      const { data, error } = await authFetch().GET('/news/latest/proveplan')
      if (error) {
        throwOnError(error)
      }
      return data ?? []
    }
  })
}

interface CreateNewsArticleMutationVariables {
  newsArticle: AddNewsArticle
  image?: File
}

export const useCreateNewsArticleMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  const addImage = useAddNewsImageMutation()
  return useMutation({
    mutationFn: async ({
      newsArticle,
      image
    }: CreateNewsArticleMutationVariables) => {
      const { data, error } = await authFetch().POST('/news', {
        body: newsArticle
      })
      if (error) {
        throwOnError(error)
      }
      if (image) {
        await addImage.mutateAsync({
          articleId: data.id,
          image
        })
      }
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['news']
      })
    }
  })
}

interface UpdateNewsArticleMutationVariables {
  articleId: number
  body: UpdateNewsArticle
}

export const useUpdateNewsArticleMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({
      articleId,
      body
    }: UpdateNewsArticleMutationVariables) => {
      const { data, error } = await authFetch().PUT('/news/{articleId}', {
        params: {
          path: { articleId }
        },
        body
      })
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['news']
      })
    }
  })
}

export const useDeleteNewsArticleMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async (articleId: number) =>
      await authFetch().DELETE('/news/{articleId}', {
        params: { path: { articleId } }
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['news']
      })
    }
  })
}

interface AddNewsImageMutationVariables {
  articleId: number
  image: File
}

export const useAddNewsImageMutation = () => {
  const queryClient = useQueryClient()
  const { authenticatedFetch } = useAuth()
  return useMutation({
    mutationFn: async ({ articleId, image }: AddNewsImageMutationVariables) => {
      const formData = new FormData()
      formData.append('file', image)
      await authenticatedFetch(`${BASE_URL}/news/${articleId}/image`, {
        body: formData,
        method: 'post'
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['news']
      })
    }
  })
}

export const useGetImagebyId = (articleId: number) => {
  const { authenticatedFetch } = useAuth()
  return useQuery({
    queryKey: ['news', articleId],
    enabled: articleId != null && articleId > 0,
    queryFn: async () => {
      const response = await authenticatedFetch(
        `${BASE_URL}/news/${articleId}/image`,
        {
          method: 'get',
          headers: {
            'Content-Type': 'application/octet-stream'
          }
        }
      )

      return await response.blob()
    }
  })
}
